import { FC, useEffect, useState } from 'react';
import { useForm, useController } from 'react-hook-form';
import { BoxV2 as Box, Flex, DropdownV2, Button } from 'portal-commons';

import { Modal } from '@/components';
import { toastFlashMessage } from '@/utils';
import { campaignController } from '@/apis/core';

interface ElectCnpDialogProps {
  open: boolean;
  onClose: (flag?: boolean) => void;
  options: Array<{ label: string; value: string }>;
  campaignUid?: string;
}

const ElectCnpDialog: FC<ElectCnpDialogProps> = ({
  open,
  onClose,
  options,
  campaignUid,
}) => {
  const [loading, setLoading] = useState(false);
  const { control, clearErrors, reset, handleSubmit } = useForm({
    defaultValues: {
      nominee: '',
    },
  });

  const {
    field: { value: cnp, onChange: setCnp },
    fieldState: { error },
  } = useController({
    control,
    name: 'nominee',
    rules: { required: 'Please elect Connectivity Partner' },
  });

  const electCnp = async () => {
    if (!campaignUid) return;

    setLoading(true);
    const response = await campaignController.electCampaignWithCnp(
      campaignUid,
      cnp
    );
    if (response) {
      onClose(true);
      toastFlashMessage('Connectivity Partner successfully elected', 'success');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!open) {
      clearErrors();
      reset();
    }
  }, [open]);

  return (
    <Modal open={open} testId="electCnpDialog">
      <Flex sx={{ flexDirection: 'column' }}>
        <Box
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: 't.black80',
            textAlign: 'center',
          }}
        >
          Elect Connectivity Partner
        </Box>
        <Box
          sx={{
            fontSize: 'H300',
            color: 't.black80',
            lineHeight: 'normal',
            mt: 'l',
            mb: 'xl',
          }}
          as="p"
        >
          Please identify the partner you will be forwarding the messaging
          campaign to. Failing to elect a CNP may hinder the campaign from
          reaching the DCA.
          <br />
          <br />
          For Sole Proprietor campaigns, if your CNP is not showing in the list,
          it means they are not enabled to receive Sole Proprietor campaigns.
          Please reach out to your CNP for more details.
        </Box>

        <Box sx={{ fontSize: 'H100', color: 't.black60', mb: 'xs' }}>
          * Indicates a Required Field
        </Box>

        <DropdownV2
          label="Elect Connectivity Partner"
          options={options}
          required
          placeholder="Select a partner"
          value={cnp}
          onChange={setCnp}
          error={!!error?.message}
          helperText={error?.message}
        />

        <Flex sx={{ justifyContent: 'center', gap: 'm', mt: 'xl', mb: 'l' }}>
          <Button variant="outline" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit(electCnp)} disabled={loading}>
            Proceed
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default ElectCnpDialog;
